import React, {useState, useEffect, useCallback} from 'react'
import {useDispatch} from 'react-redux'

import BASELoading from "../../../../../../../@BASE/core/BASELoading";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import BASESvgIcon from "../../../../../../../@BASE/core/BASESvgIcon";

import MediaAttribution from "../components/MediaAttribution";
import Instructions from "../components/Instructions";

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { getInstance } from "../../../../../../actions";

export default function AssessmentListeningMC(props) {
  const { data, dir, ins = [], course, comments = [] } = props;
  const tl = `${dir === "rtl" ? "text-18" : "text-14"} ${course?.language?.acronym}`.trim();

  const dispatch = useDispatch();
  const [isFetched, setIsFetched] = useState(false);
  const [materialPage, setMaterialPage] = useState();

  if (!data) {
    return <BASELoading />;
  }

  const getMaterialPage = useCallback(
    async (page_id) => {
      try {
        const response = await dispatch(getInstance("pages")(page_id)).unwrap();
        return response;
      } catch (error) {
        console.error("Failed to fetch lesson pages:", error);
        return null;
      }
    },
    [dispatch] // Memoizing the function to prevent re-creation
  );

  const formatVttTime = (seconds) => {
			const h = Math.floor(seconds / 3600);
			const m = Math.floor((seconds % 3600) / 60);
			const s = Math.floor(seconds % 60);
			const ms = Math.floor((seconds - Math.floor(seconds)) * 1000);
			const hh = h.toString().padStart(2, '0');
			const mm = m.toString().padStart(2, '0');
			const ss = s.toString().padStart(2, '0');
			const mmm = ms.toString().padStart(3, '0');
			return `${hh}:${mm}:${ss}.${mmm}`;
	};

	// render cc tracks for the video
	const renderCC = () => {
		let createdTrackBlobUrl = null;

		if (materialPage.content.cc && materialPage.content.cc.length > 0) {
			try {
				// Build a WebVTT string
				let vtt = 'WEBVTT\n\n';
				materialPage.content.cc.forEach((item, idx) => {
					// Compute start/end in seconds (fallback to 0)
					const start =
							(item.start_minute || 0) * 60 + (item.start_second || 0);
					const end = (item.end_minute || 0) * 60 + (item.end_second || 0);
					const startTime = formatVttTime(start);
					const endTime = formatVttTime(end);
					// sanitize the text - replace HTML tags/newlines with plain text line breaks
					const text = (item.tl_text || item.text || '')
						.replace(/<\/?[^>]+(>|$)/g, '')
						.replace(/\r\n|\r|\n/g, '\n');
					vtt += `${idx + 1}\n${startTime} --> ${endTime}\n${text}\n\n`;
				});

				// create a blob url and append a <track> element to the video
				const blob = new Blob([vtt], { type: 'text/vtt' });
				createdTrackBlobUrl = URL.createObjectURL(blob);
				return <track kind="subtitles" label="CC" srcLang="en" src={createdTrackBlobUrl} default />;
				// Note: appending a <track> often makes browsers show native captions UI.
			} catch (err) {
				console.warn('Failed to create VTT track:', err);
			}
    }
		return null;
	}

  useEffect(() => {
    if (!data.media_page_id || isFetched) return;
  
    setIsFetched(true);
  
    const fetchPage = async () => {
      const page = await getMaterialPage(data.media_page_id);
      if (page) {
        setMaterialPage(page); // wrap in array if your state expects an array
      }
    };
  
    fetchPage();
  }, [data.media_page_id, getMaterialPage, isFetched]);

  // filer comments by component_id
  const componentCommentsCount = (component) => {
    return comments.filter(
      (comment) =>
        comment.component === component && comment.internality !== true
    ).length;
  };

  return (
    <div>
      <style>
        {`
          .wysiwyg p {
            margin: 0px;
          }
        `}
      </style>

      <Instructions
        ins={ins}
        course={course}
        dir={dir}
        tl={tl}
        componentCommentsCount={componentCommentsCount}
      />

      <Typography className="text-16 font-bold p-16 pb-0">
        Page Content:
      </Typography>

      <Typography className="text-14 font-bold p-16 pb-0">Materials</Typography>
      <Paper elevation={0} variant="outlined" square className="p-16 m-16">
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Typography className="text-14 font-bold m-0">
              Authentic Material:
            </Typography>
            <Divider className="my-16" />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={0}>
              <Grid item xs={12} sm={4} lg={2}>
                <Typography className="text-14 font-semibold m-0">
                  Title (TL):
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8} lg={10}>
                <Typography className={`${tl} m-0`} dir={dir}>
                  {data.media_title_tl}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} lg={2}>
                <Typography className="text-14 font-semibold m-0">
                  Title (EN):
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8} lg={10}>
                <Typography className="text-14 m-0">
                  {data.media_title_en}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} lg={2}>
                <Typography className="text-14 font-semibold m-0">
                  Source URL:
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8} lg={10}>
                <Typography className="text-14 m-0">
                  {data.source_url}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} lg={2}>
                <Typography className="text-14 font-semibold m-0">
                  Material location:
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8} lg={10}>
                <Typography className="text-14 m-0">
                  {data.material_location}
                </Typography>
              </Grid>
              {materialPage && (
                <Grid item xs={12}>
                  <Divider className="my-16" />
                  <Accordion className="w-full shadow-none">
                    <AccordionSummary
                      className="p-0"
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="material-content"
                      id="material-header"
                      square
                    >
                      <Typography component="span">Media</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box className='flex justify-center p-16 border-1 mx-0 w-full' sx={{ backgroundColor: '#F1F5F9' }}>
                        <div className="w-[640px] h-[360px] flex items-center justify-center overflow-hidden">
                            <video width="640" height="360" controls>
                              <source src={materialPage.content.media.media_url} type="video/mp4" />
                              {renderCC()}
                            </video>
                        </div>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} variant="outlined" square className="m-16">
        <Paper elevation={0} variant="outlined" square className="p-16 m-16">
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Grid container spacing={0}>
                <Grid item xs={12}>
                  <Typography className="text-14 font-bold m-0">
                    Question:
                  </Typography>
                  <Divider className="my-16" />
                </Grid>
                <Grid item xs={12}>
                  <Typography className={`${tl} m-0`} dir={dir}>
                    {data.question.prompt}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        {/* <Typography className="text-14 font-bold p-16 pb-0">
          Timestamp:
        </Typography>
        <Paper elevation={0} variant="outlined" square className="p-16 m-16">
          <Grid container spacing={0.5} columns={24}>
            <Grid item xs={4} sm={3}>
              <Typography className="text-14 font-semibold m-0">
                Start Time:
              </Typography>
            </Grid>
            <Grid item xs={20} sm={21}>
              <Typography className="text-14 m-0">
                {data.question.start_minute < 10
                  ? `0${data.question.start_minute}`
                  : data.question.start_minute}{" "}
                :{" "}
                {data.question.start_second < 10
                  ? `0${data.question.start_second}`
                  : data.question.start_second}
              </Typography>
            </Grid>
          </Grid>
          <Grid container spacing={0.5} columns={24}>
            <Grid item xs={4} sm={3}>
              <Typography className="text-14 font-semibold m-0">
                End Time:
              </Typography>
            </Grid>
            <Grid item xs={20} sm={21}>
              <Typography className="text-14 m-0">
                {data.question.end_minute < 10
                  ? `0${data.question.end_minute}`
                  : data.question.end_minute}{" "}
                :{" "}
                {data.question.end_second < 10
                  ? `0${data.question.end_second}`
                  : data.question.end_second}
              </Typography>
            </Grid>
          </Grid>
        </Paper> */}

        <Paper elevation={0} variant="outlined" square className="p-16 m-16">
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography className="text-14 font-bold m-0">Lures:</Typography>
              <Divider className="my-16" />
              <Grid container spacing={0}>
                {data.question.lures.map((lure, index) => (
                  <Grid key={`lure-${lure.lure_text}-${index}`} item xs={12}>
                    {index > 0 && <Divider className="my-8" />}
                    <Grid container spacing={1} columns={24}>
                      <Grid item xs={4}>
                        {lure.is_correct ? (
                          <Grid container direction="row">
                            <Typography className="text-14 font-semibold m-0">
                              Correct
                            </Typography>
                            <BASESvgIcon
                              className="text-green-600 mx-2"
                              size={20}
                            >
                              heroicons-solid:check-circle
                            </BASESvgIcon>
                            <Typography className="text-14 font-semibold m-0">
                              Lure:
                            </Typography>
                          </Grid>
                        ) : (
                          <Grid container direction="row">
                            <Typography className="text-14 font-semibold m-0">
                              Incorrect
                            </Typography>
                            <BASESvgIcon
                              className="text-red-600 mx-2"
                              size={20}
                            >
                              heroicons-solid:x-circle
                            </BASESvgIcon>
                            <Typography className="text-14 font-semibold m-0">
                              Lure:
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                      <Grid item xs>
                        <Typography
                          className={`${tl} m-0 ${
                            index < data.question.lures.length - 1 && `mb-8`
                          }`}
                          dir={dir}
                        >
                          {lure.lure_text}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={0} variant="outlined" square className="p-16 m-16">
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography className="text-14 font-bold m-0">
                Question Options:
              </Typography>
              <Divider className="my-16" />
            </Grid>
            <Grid item xs={2}>
              <Typography className="text-14 font-semibold m-0">
                Multiple Select:
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography className="text-14 m-0">
                {data.question.multi_select ? "true" : "false"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Paper>
    </div>
  );
}
